import React, { useEffect, useState, useRef } from "react";
import styles from "./NumberAssign.module.css";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import { useLocation, useNavigate } from "react-router-dom";
import PopUp from "../Popup/Popup";
import { useAgentStore } from "../../Store/agentDetailStore";
import {
    createNumberOrder,
    createVobizNumberOrder,
    fetchAgentDetailById,
    fetchAvailablePhoneNumberByCountry,
    fetchAvailableVobizNumbers,
    updateAgent,
} from "../../Store/apiStore";
import { usesVobiz } from "../../utils/apiBackend";
import GoogleAddressInputWithDropdown from "../GoogleAddressInputWithDropdown/GoogleAddressInputWithDropdown";
import axios from "axios";
import { API_BASE_URL } from "../../Store/apiStore";
import decodeToken from "../../lib/decodeToken";
// new
const features = [
    "No long-term contract",
    "Cancel anytime from settings",
    "Your number is yours while active",
];

const NumberAssign = ({
    // Wizard integration — intercepts post-purchase navigation without
    // touching the existing /number-assign route consumers.
    wizardMode = false,
    onWizardNext,
    onWizardBack,
    /*
     * Candidate-mode (GoLiveWizard only).
     *
     * When true, the CTA does NOT place a Telnyx order, does NOT write
     * to the agent row, and does NOT show the "Number assigned
     * successfully" popup. Instead it forwards the picked number +
     * derived locality/region to `onWizardCandidatePicked` so the
     * wizard can stash it as a candidate and defer the actual purchase
     * to the user's "Confirm Number" tap on the ownership screen.
     *
     * Off by default — PostPurchase + the standalone /number-assign
     * route keep their existing buy-on-CTA behaviour.
     */
    wizardCandidateMode = false,
    onWizardCandidatePicked,
} = {}) => {
    const {
        agentData,
        assignedNumbers,
        totalBookings,
        setAgentById,
        setCurrentAgentId,
        getAgentById,
    } = useAgentStore();
    const [availableNumbers, setAvailableNumbers] = useState([]);
    const [selectedNumber, setSelectedNumber] = useState("");
    const [loading, setLoading] = useState(false);
    const [locationAddress, setLocationAddress] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [country, setCountry] = useState("");
    // Existing voip on the agent — populated when the visitor returns
    // to this step (e.g. Back from Ownership). When present we skip the
    // Telnyx order and just forward the wizard so we don't buy a second
    // number.
    const [existingVoip, setExistingVoip] = useState("");
    const [changeMode, setChangeMode] = useState(false);
    // Custom "Find More Numbers" dropdown state — we moved off the native
    // <select> because its dropdown width was locked to the 30% parent,
    // truncating international numbers (+1-etc.). Our panel is 240px wide
    // and scrolls when the Telnyx list is long.
    const [numbersDropdownOpen, setNumbersDropdownOpen] = useState(false);
    const numbersDropdownRef = useRef(null);
    const agentBuisnesId = sessionStorage.getItem("SelectAgentBusinessId");
    const isInitialLoad = useRef(true);
    const hasSetInitialAddress = useRef(false);
    const debounceTimeout = useRef(null);
    const googleInputRef = useRef(null);
    const paygActivationRanRef = useRef(false);
    const paygEmailTriggeredRef = useRef(false);
    const navigate = useNavigate();
    const location = useLocation();

    const [showPopup, setShowPopup] = useState(false);
    const [popupType, setPopupType] = useState("");
    const [popupMessage, setPopupMessage] = useState("");
    const token = localStorage.getItem("token") || "";
    const decodeTokenData = decodeToken(token);
    const agentId = sessionStorage.getItem("SelectAgentId");
    const userId = decodeTokenData?.id;

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const isPayg = queryParams.get("ispayg");
        if (isPayg !== "true") return;
        if (paygActivationRanRef.current) return;
        paygActivationRanRef.current = true;
        queryParams.delete("ispayg");
        const qs = queryParams.toString();
        const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        localStorage.removeItem("isPayg");

        const activatePayg = async () => {
            try {
                if (!decodeTokenData || !token) return;

                const customerId = decodeTokenData.customerId;

                const response = await axios.post(
                    `${API_BASE_URL}/pay-as-you-go-saveAgent`,
                    {
                        customerId,
                        agentId,
                        status: "active",
                        isFree: true,
                        isByAdmin: false,
                        isQuickSetup: true,
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    setPopupType("success");
                    setPopupMessage("Payment successful! Your PAYG subscription is now active.");
                    setShowPopup(true);
                    // if (!paygEmailTriggeredRef.current && userId && agentId) {
                    //     paygEmailTriggeredRef.current = true;

                    //     const resp = await fetch(`${API_BASE_URL}/notifications/number-purchased`, {
                    //         method: "POST",
                    //         headers: {
                    //             "Content-Type": "application/json",
                    //             Authorization: `Bearer ${token}`,
                    //         },
                    //         body: JSON.stringify({ userId, agentId }),
                    //     });

                    // }
                }
            } catch (error) {
                console.error("PAYG activation error:", error.response?.data || error.message);
                setPopupType("failed");
                setPopupMessage("Something went wrong while activating PAYG.");
                setShowPopup(true);
            }
        };

        activatePayg();
    }, [location.search, agentId, userId, token, decodeTokenData]);

    const handleClosePopup = () => {
        setShowPopup(false);
    };

    // Click-outside to close the custom "Find More Numbers" dropdown.
    useEffect(() => {
        if (!numbersDropdownOpen) return;
        const onDocClick = (e) => {
            if (
                numbersDropdownRef.current &&
                !numbersDropdownRef.current.contains(e.target)
            ) {
                setNumbersDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [numbersDropdownOpen]);

    // Single backend call — telnyxController now runs the 3-tier
    // cascade (city+state → state-only → country-only) server-side and
    // returns whichever tier produced numbers, plus a `fallback_stage`
    // hint we can use for "showing state-wide / country-wide" UX.
    // Earlier this lived in the frontend as a 2-tier (city+state →
    // country-only) chain that skipped the more useful state-only
    // intermediate.
    const fetchPhoneNumbers = async (countryCode, cityName, stateCode, showPopupOnSuccess = true) => {
        const isVobiz = usesVobiz() || countryCode === "IN";
        if (!countryCode && !isVobiz) return;
        setLoading(true);

        try {
            const res = isVobiz
                ? await fetchAvailableVobizNumbers(
                    token,
                    "IN",
                    cityName || "Bangalore",
                    stateCode || "KA",
                    null,
                    null,
                )
                : await fetchAvailablePhoneNumberByCountry(
                    token,
                    countryCode,
                    cityName || "",
                    stateCode || "",
                    null,
                    null,
                );

            if (res?.data?.length > 0) {
                setAvailableNumbers(res.data);
                setSelectedNumber(res.data[0].phone_number);
            } else {
                setAvailableNumbers([]);
                setSelectedNumber("");
            }
        } catch (error) {
            console.error("Failed to fetch phone numbers:", error);
            setAvailableNumbers([]);
            setSelectedNumber("");
            setPopupType("error");
            setPopupMessage("Failed to fetch available phone numbers. Please try again.");
            setShowPopup(true);
        } finally {
            setLoading(false);
        }
    };

    const parseAddressAndFetchNumbers = (address) => {
        if (!address || address.length < 3) return;

        setLoading(true);

        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ address: address }, (results, status) => {
            if (status === "OK" && results[0]) {
                let cityName = "";
                let stateCode = "";
                let countryCode = "";

                results[0].address_components.forEach((component) => {
                    if (component.types.includes("locality")) {
                        cityName = component.long_name;
                    }
                    if (component.types.includes("administrative_area_level_1")) {
                        stateCode = component.short_name;
                    }
                    if (component.types.includes("country")) {
                        countryCode = component.short_name;
                    }
                });

                // console.log("Parsed location:", { cityName, stateCode, countryCode });

                if (countryCode || usesVobiz()) {
                    const resolvedCountry = countryCode || (usesVobiz() ? "IN" : "");
                    const resolvedCity = cityName || (usesVobiz() ? "Bangalore" : "");
                    const resolvedState = stateCode || (usesVobiz() ? "KA" : "");
                    setCity(resolvedCity);
                    setState(resolvedState);
                    setCountry(resolvedCountry);

                    fetchPhoneNumbers(resolvedCountry, resolvedCity, resolvedState);
                } else {
                    setLoading(false);
                    setPopupType("error");
                    setPopupMessage("Unable to determine location. Please try a different address.");
                    setShowPopup(true);
                }
            } else {
                console.error("Geocoding failed:", status);
                setLoading(false);

                if (status !== "ZERO_RESULTS") {
                    setPopupType("error");
                    setPopupMessage("Unable to fetch location details. Please try again.");
                    setShowPopup(true);
                }
            }
        });
    };

    // Initial load - Extract from knowledge_base_texts if needed
    useEffect(() => {
        if (!agentId || !isInitialLoad.current) return;

        const fetchAndSetBusinessDetails = async () => {
            let businessDetails = null;
            let foundExistingVoip = null;

            // First try to get from store
            const res = getAgentById(agentId);
            let localAgentInfo = res?.agentData;

            if (localAgentInfo?.business) {
                businessDetails = localAgentInfo.business;
            } else {
                // If not in store, fetch from API
                try {
                    const agentDetails = {
                        agentId: agentId,
                        bussinesId: agentBuisnesId
                    };

                    const response = await fetchAgentDetailById(agentDetails);
                    localAgentInfo = response?.data;

                    if (localAgentInfo?.business) {
                        businessDetails = localAgentInfo.business;

                        // Save to store
                        let numbersArray = [];
                        let total = 0;
                        setAgentById(agentDetails.agentId, localAgentInfo, numbersArray, total);
                    }
                } catch (err) {
                    console.error("Failed to fetch data", err.response || err.message || err);
                    setPopupType("error");
                    setPopupMessage("Failed to load business details. Please try again.");
                    setShowPopup(true);
                    return; // Exit early on error
                }
            }

            // ── Detect existing voip_numbers on the agent ──
            // Users who come BACK into this step already have a number assigned;
            // we surface it so they can Continue without triggering a second order.
            try {
                const rawVoip = localAgentInfo?.agent?.voip_numbers;
                const parsed = typeof rawVoip === "string"
                    ? JSON.parse(rawVoip)
                    : rawVoip;
                const existing = Array.isArray(parsed)
                    ? parsed[0]
                    : parsed;
                if (existing) {
                    setExistingVoip(existing);
                    foundExistingVoip = existing;
                }
            } catch {
                /* ignore parse errors — falls through to fresh flow */
            }

            // Process business details if available
            if (businessDetails) {
                let countryCode = businessDetails.country_code || "";
                let stateCode = businessDetails.state_code || "";
                let cityName = businessDetails.city || "";
                let businessAddress = businessDetails.address1 || "";

                // Extract from knowledge_base_texts if state_code is null
                if (!stateCode && businessDetails.knowledge_base_texts) {
                    try {
                        const knowledgeBase = JSON.parse(businessDetails.knowledge_base_texts);
                        stateCode = knowledgeBase.state_code || "";

                        if (!cityName && knowledgeBase.city) {
                            cityName = knowledgeBase.city;
                        }

                        if (!businessAddress && knowledgeBase.address) {
                            businessAddress = knowledgeBase.address;
                        }

                        // console.log("Extracted from knowledge_base:", { stateCode, cityName, businessAddress });
                    } catch (error) {
                        console.error("Error parsing knowledge_base_texts:", error);
                    }
                }

                // console.log("Final extracted values:", { countryCode, stateCode, cityName });

                if (usesVobiz()) {
                    if (!countryCode) countryCode = "IN";
                    if (!cityName) cityName = "Bangalore";
                    if (!stateCode) stateCode = "KA";
                }

                setCountry(countryCode);
                setState(stateCode);
                setCity(cityName);

                // Format address for display
                let fullAddress = "";
                if (businessAddress) {
                    fullAddress = businessAddress;
                } else if (cityName && stateCode) {
                    fullAddress = `${cityName}, ${stateCode}, ${countryCode}`;
                } else if (cityName) {
                    fullAddress = `${cityName}, ${countryCode}`;
                } else {
                    fullAddress = countryCode || (usesVobiz() ? "Bangalore, KA, IN" : "");
                }

                // console.log("Setting initial address:", fullAddress);

                setLocationAddress(fullAddress);
                hasSetInitialAddress.current = true;

                if (countryCode || usesVobiz()) {
                    const finalCountry = countryCode || "IN";
                    // Skip fetching a new number list if the agent already has
                    // one assigned — we only show it, not replace it.
                    if (!foundExistingVoip) {
                        fetchPhoneNumbers(finalCountry, cityName, stateCode, false);
                    }
                    isInitialLoad.current = false;
                } else {
                    setPopupType("warning");
                    setPopupMessage("Business location incomplete. Please search for a location.");
                    setShowPopup(true);
                }
            } else {
                // console.log(" No business details found");
                setPopupType("warning");
                setPopupMessage("Business information not found. Please try again.");
                setShowPopup(true);
            }
        };

        fetchAndSetBusinessDetails();
    }, [agentId, agentBuisnesId, getAgentById, setAgentById]);
    const handleLocationChange = (newAddress) => {
        // console.log("Location changed to:", newAddress);
        setLocationAddress(newAddress);
    };

    useEffect(() => {
        // Don't fetch new numbers if agent already has one and user hasn't
        // explicitly opted to change it — this is the wizard lock mechanism.
        if (existingVoip && !changeMode) return;
        if (isInitialLoad.current || !locationAddress || locationAddress.length < 3) return;

        if (hasSetInitialAddress.current) {
            hasSetInitialAddress.current = false;
            return;
        }

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            // console.log("🔍 User searched for:", locationAddress);
            parseAddressAndFetchNumbers(locationAddress);
        }, 1500);

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [locationAddress, existingVoip, changeMode]);
    const handleBuyNumber = async () => {
        try {
            // If the agent already has a voip and the user hasn't opted to
            // change it, skip the Telnyx order + DB write and just advance.
            // This is the common path when the user lands here via Back
            // from a later wizard step.
            if (existingVoip && !changeMode) {
                if (wizardMode && typeof onWizardNext === "function") {
                    onWizardNext(existingVoip);
                    return;
                }
                navigate("/immediate-owner-ship", { replace: true });
                return;
            }

            // Resolve locality/region off the picked Telnyx number — used
            // by both the candidate-mode handoff (so ownership shows the
            // NUMBER's city) and the legacy purchase path (so the same
            // value lands in voip_number_city / voip_number_state on the
            // agent row).
            //
            // Resolution priority:
            //   1. Telnyx region_information on the picked number
            //      (rate-center / locality + state). Most accurate.
            //   2. The user's typed/searched city + state from the
            //      location box (Google geocode result). Good fallback
            //      because it reflects what the user was searching for.
            //   3. Empty string — caller falls back to business.city
            //      downstream. Safe no-op.
            const pickedObj = availableNumbers.find(
                (n) => n.phone_number === selectedNumber,
            );
            const regions = pickedObj?.region_information || [];
            const cityRegion = regions.find(
                (r) =>
                    r.region_type === "rate_center" ||
                    r.region_type === "rate-center" ||
                    r.region_type === "city" ||
                    r.region_type === "locality",
            );
            const stateRegion = regions.find(
                (r) =>
                    r.region_type === "state" ||
                    r.region_type === "administrative_area",
            );
            // Capitalize "OWENSBORO" → "Owensboro" for display niceness
            const titleCase = (s) =>
                String(s || "")
                    .toLowerCase()
                    .replace(/\b\w/g, (c) => c.toUpperCase());
            const isVobizNumber =
                usesVobiz() ||
                country === "IN" ||
                (selectedNumber && selectedNumber.startsWith("+91"));
            const resolvedVoipCity = isVobizNumber
                ? (city || pickedObj?.city || pickedObj?.locality || "Bangalore")
                : (titleCase(cityRegion?.region_name) || city || "");
            const resolvedVoipState = isVobizNumber
                ? (state || pickedObj?.state || pickedObj?.region || "KA")
                : ((stateRegion?.region_name || state || "").toUpperCase());

            /*
             * CANDIDATE MODE (GoLiveWizard edit-flow).
             *
             * Hand the pick back to the wizard without touching Telnyx / Vobiz
             * or the DB — wizard stashes it as a candidate; the actual
             * purchase happens when the user taps "Confirm Number" on
             * the ownership screen. Also intentionally skips the
             * "Number assigned successfully" popup since nothing was
             * actually purchased — the user would get confirmation
             * AFTER Confirm Number on the next step.
             */
            if (wizardCandidateMode && typeof onWizardCandidatePicked === "function") {
                onWizardCandidatePicked({
                    phone_number: selectedNumber,
                    locality: resolvedVoipCity,
                    region: resolvedVoipState,
                });
                return;
            }

            if (process.env.REACT_APP_ENV === "production") {
                const orderRes = isVobizNumber
                    ? await createVobizNumberOrder(selectedNumber, agentId)
                    : await createNumberOrder(selectedNumber, agentId);

                if (orderRes && orderRes.success === false) {
                    setShowPopup(true);
                    setPopupType("failed");
                    setPopupMessage(
                        orderRes.message || "Could not purchase the number. Please try again."
                    );
                    return;
                }
            }

            await updateAgent(agentId, {
                voip_numbers: [selectedNumber],
                voip_numbers_created: new Date(),
                assignNumFree: 1,
                isNumActivated: 1,
                isDeactivated: false,
                agentStatus: true,
                ...(resolvedVoipCity ? { voip_number_city: resolvedVoipCity } : {}),
                ...(resolvedVoipState ? { voip_number_state: resolvedVoipState } : {}),
            });
            setShowPopup(true)
            setPopupType("success")
            setPopupMessage("Number assigned successfully")
            setTimeout(() => {
                if (wizardMode && typeof onWizardNext === "function") {
                    onWizardNext(selectedNumber);
                    return;
                }
                navigate("/immediate-owner-ship", { replace: true })
            }, 2000);

        } catch (error) {

        }
    }
    return (
        <>
            {showPopup && (
                <PopUp
                    type={popupType}
                    onClose={handleClosePopup}
                    message={popupMessage}
                />
            )}
            <div
                className={styles.containerBox}
                // wizardMode: CSS .containerBox has min-height: 100dvh +
                // .looksGoodBtn is absolute (bottom: 4%). Inside the wizard
                // that pushes the CTA under the fixed footer. Let content
                // size naturally + flow the button below via overrides on
                // the button div.
                style={wizardMode ? { minHeight: "auto", paddingBottom: 140 } : undefined}
            >
                <div className={styles.container}>
                    <div className={styles.titleBar}>
                        <h4 className={styles.lightTitle}>Your Local Number</h4>
                        <h5 className={styles.boldTitle}>for Receptionist</h5>
                        <p className={styles.subtitle}>
                            {existingVoip && !changeMode
                                ? "Your agent already has a number. Continue to the next step."
                                : wizardCandidateMode
                                ? "Pick a different local number. We'll only reserve it once you confirm on the next step."
                                : "We have assigned the best available phone number for your agent. You can choose another option from the list"}
                        </p>
                    </div>

                    <div className={styles.numberDiv}>
                        {loading ? (
                            <h6>Loading numbers...</h6>
                        ) : existingVoip && !changeMode ? (
                            <h6>{existingVoip}</h6>
                        ) : (
                            <h6>{selectedNumber || "No number available"}</h6>
                        )}
                    </div>

                    {/* User requested to remove the change number option so they can't purchase a new one if already assigned
                    {existingVoip && !changeMode && (
                        <button
                            type="button"
                            onClick={() => setChangeMode(true)}
                            style={{
                                display: "block",
                                margin: "12px auto 0",
                                background: "transparent",
                                border: "none",
                                color: "#6524EB",
                                fontSize: 13,
                                fontWeight: 600,
                                textDecoration: "underline",
                                cursor: "pointer",
                            }}
                        >
                            Change number
                        </button>
                    )}
                    */}

                    <div
                        className={styles.findNumDiv}
                        // Hide the location search / find-more dropdown when
                        // showing an existing number — the user isn't here
                        // to re-pick unless they explicitly click "Change".
                        style={existingVoip && !changeMode ? { display: "none" } : undefined}
                    >
                        {/* India ka pool ek hi hai, to chunne ke liye koi city
                            hai hi nahi — location search sirf US/CA pe. Wahi
                            gate jo ReceptionistNumber aur ImmediateOwnerShip
                            pe hai. */}
                        {!usesVobiz() && (
                            <div className={styles.DropDownBar}>
                                <GoogleAddressInputWithDropdown
                                    ref={googleInputRef}
                                    value={locationAddress}
                                    onChange={handleLocationChange}
                                    placeholder="Search location (e.g., Irvine, CA)"
                                    className={styles.locationInut}
                                />
                            </div>
                        )}

                        <div className={styles.DropDownBar2} ref={numbersDropdownRef}>
                            {/* Visible trigger — clicking toggles the custom
                                dropdown below. Native <select> removed to
                                avoid browser-locked width (which truncated
                                international numbers). */}
                            <button
                                type="button"
                                className={styles.findTriggerBtn}
                                onClick={() => {
                                    if (
                                        !loading &&
                                        availableNumbers.length > 0
                                    ) {
                                        setNumbersDropdownOpen((o) => !o);
                                    }
                                }}
                                disabled={loading || availableNumbers.length === 0}
                            >
                                <span className={styles.findText}>Find More</span>
                                <span className={styles.findText}>Numbers</span>
                            </button>

                            {numbersDropdownOpen && (
                                <div
                                    className={styles.numberDropdown}
                                    role="listbox"
                                >
                                    {availableNumbers.length === 0 ? (
                                        <div className={styles.numberDropdownEmpty}>
                                            No numbers available
                                        </div>
                                    ) : (
                                        availableNumbers.map((num, index) => {
                                            const isActive =
                                                num.phone_number === selectedNumber;
                                            return (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={isActive}
                                                    className={`${styles.numberDropdownItem} ${
                                                        isActive
                                                            ? styles.numberDropdownItemActive
                                                            : ""
                                                    }`}
                                                    onClick={() => {
                                                        setSelectedNumber(num.phone_number);
                                                        setNumbersDropdownOpen(false);
                                                    }}
                                                >
                                                    {num.phone_number}
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.featuresDiv}>
                        {features.map((text, index) => (
                            <div className={styles.row} key={index}>
                                <div className={styles.circle}>{index + 1}</div>
                                <p className={styles.text}>{text}</p>
                            </div>
                        ))}
                    </div>

                    <div 
                        className={styles.looksGoodBtn}
                        style={wizardMode ? { position: "fixed", bottom: "64px", left: "50%", transform: "translateX(-50%)", width: "calc(100% - 32px)", maxWidth: "468px", zIndex: 60 } : undefined}
                    >
                        <p className={styles.desc}>
                            {wizardCandidateMode
                                ? "Confirm on the next step to reserve this number."
                                : "You'll be guided to forward your calls next."}
                        </p>
                        <AnimatedButton
                            label={
                                existingVoip && !changeMode
                                    ? "Continue"
                                    : wizardCandidateMode
                                    ? "Use this Number"
                                    : "Assign Phone Number"
                            }
                            onClick={handleBuyNumber}
                            disabled={(existingVoip && !changeMode) ? false : (!selectedNumber || loading)}
                            positionType={wizardMode ? "relative" : undefined}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default NumberAssign;